<template>
	<div>
		<div class="meta-label-with-action">
			<span class="meta-label" v-if="!isSelectionMode">Asociaciones</span>
			<v-menu
				v-model="associationsMenu"
				:offset-y="true"
				:close-on-content-click="false"
				:nudge-left="$vuetify.breakpoint.mobile ? nudgeLeft : 0"
				:max-width="$vuetify.breakpoint.mobile ? '100vw' : 'auto'"
				:min-width="$vuetify.breakpoint.mobile ? '100vw' : 'auto'"
			>
				<template v-slot:activator="{ on, attrs }">
					<button
						v-bind="attrs"
						v-on="on"
						type="button"
						:class="['icon-action-btn', 'ml-2', { 'add-assoc-btn': isSelectionMode }]"
					>
						<v-icon size="14" color="#2D4B95" v-if="!isSelectionMode">mdi-plus-circle</v-icon>
						<template v-else>
							<span class="add-assoc-content">
								<v-icon size="18" color="#ffffff" class="mr-2">mdi-plus</v-icon>
								Agregar Asociación
							</span>
						</template>
					</button>
				</template>
				<div class="search-dropdown pa-3">
					<v-text-field
						v-model="searchForAssociation"
						label="Búsqueda"
						outlined
						hide-details
						class="custom-text-field-minify search-field"
						style="width: 100%; font-size: 12px;"
						dense
						clearable
						@keydown="sendToSearch"
						@click:clear="clearSearch"
					>
						<template slot="prepend-inner">
							<v-icon small color="#5e85ef" class="mr-1">mdi-magnify</v-icon>
						</template>
						<template slot="append">
							<v-progress-circular
								v-if="searching"
								:size="16"
								:width="2"
								color="#5e85ef"
								indeterminate
							></v-progress-circular>
						</template>
					</v-text-field>

					<AssociationSearchResult
						v-if="showResults"
						:searching="searching"
						:searchResult="searchResult"
						:closeResults="closeResults"
						:selectedItems="allAssociations"
						:removeAssociation="isSelectionMode ? startRemoveSelection : startRemoveAssociation"
					/>

					<div v-else class="assoc-empty-state">
						<v-icon size="22" color="#c2cbe0">mdi-text-search</v-icon>
						<span class="assoc-empty-state__text">
							Escribe para buscar RDIs, tareas, entregas o adicionales
						</span>

						<div class="assoc-divider">
							<span>o</span>
						</div>

						<button type="button" class="assoc-file-btn" @click="openFileSelector">
							<v-icon size="16" color="#5e85ef" class="mr-2">mdi-paperclip</v-icon>
							Seleccionar archivo desde biblioteca
						</button>
					</div>
				</div>
			</v-menu>
		</div>

			<div class="assoc-content mt-1" v-if="allAssociations.length > 0">
				<div class="files-wrapper" v-if="visibleFileAssociations.length > 0">
					<AssociationFileCard
						v-for="association in visibleFileAssociations"
						:key="`file-${association.association_id}`"
						:association="association"
						@click="openFileAssociation(association)"
						@remove="handleRemoveAssociation(association)"
					/>
				</div>

				<div class="chips-wrapper" v-if="visibleTextAssociations.length > 0">
					<AssociationPill
						v-for="association in visibleTextAssociations"
						:key="`text-${association.association_id}`"
						:association="association"
						@click="handleMoveTo(association)"
						@remove="handleRemoveAssociation(association)"
					/>
				</div>

				<button v-if="hiddenCount > 0" type="button" class="assoc-more-btn" @click="showAssociationsPanel = true">
					<v-icon size="13" color="#5e85ef" class="mr-1">mdi-dots-horizontal</v-icon>
					Ver todas ({{ allAssociations.length }})
				</button>
			</div>

		<FileSelectorModal
			v-if="showFileSelector"
			@select="onFileSelected"
			@close="showFileSelector = false"
		/>

		<AssociationsPanel
			v-if="showAssociationsPanel"
			:textAssociations="textAssociations"
			:fileAssociations="fileAssociations"
			@open-text="handlePanelOpenText"
			@open-file="handlePanelOpenFile"
			@remove="handlePanelRemove"
			@close="showAssociationsPanel = false"
		/>

    <GlobalFileViewer
      :media-items="viewerMediaItems"
      :media-index="viewerMediaIndex"
      :document-item="viewerDocumentItem"
      :forge-item="viewerForgeItem"
			@open-morphable="openItemMorphable"
			@minimize-file="minimizeViewerFile"
			@close="closeViewers"
			@onChange="onChangeMediaIndex"
			@error="onViewerError"
			@load="onViewerLoad"
		/>

		<AcceptCancelModal
			:acceptRejectModal="forgeConfirmModal"
			:acceptRejectModalData="forgeConfirmData"
			:closeModal="handleForgeConfirmation"
		/>
	</div>
</template>

<script>
import _debounce from 'lodash/debounce'

import AssociationSearchResult from '@/components/Globals/Helpers/AssociationSearchResult'
import FileSelectorModal from '@/components/Globals/Modals/General/FileSelectorModal'
import AssociationsPanel from '@/components/Globals/Helpers/ItemsAssociations/AssociationsPanel'
import AssociationPill from '@/components/Globals/Helpers/ItemsAssociations/AssociationPill'
import AssociationFileCard from '@/components/Globals/Helpers/ItemsAssociations/AssociationFileCard'
import GlobalFileViewer from '@/components/Globals/Helpers/GlobalFileViewer'
import AcceptCancelModal from '@/components/Globals/Modals/General/AcceptCancelModal'

import { checkPermissions } from '@/helpers/permissions'
import {search} from '@/helpers/api/general'
import {getTareaByID} from '@/helpers/api/tasks'
import {getRdiByID} from '@/helpers/api/rdi'
import { getEntregaByID, getConfig } from "@/helpers/api/entrega";
import { getJobTypes } from "@/helpers/api/user";
import {addItemsAssociations, removeItemsAssociations} from '@/helpers/api/associations'
import { getParentElement } from '@/helpers/api/general'
import { getSupportedExtensions, addFileToForge } from '@/helpers/api/forge'
import { isForgeItem as isForgeCompatible } from '@/helpers/forge'

const TEXT_INLINE_LIMIT = 4
const FILE_INLINE_LIMIT = 4

export default {
	name: 'ItemsAssociationInput',
	components: {
		AssociationSearchResult,
		FileSelectorModal,
		AssociationsPanel,
		AssociationPill,
		AssociationFileCard,
		GlobalFileViewer,
		AcceptCancelModal
	},
	props: {
		item: {
			type: Object,
			required: false,
			default: null
		},
		item_type: {
			type: String,
			required: false,
			default: ''
		},
		// Selection mode: when true (or when `changeItems` is provided)
		// the component works as an in-memory selector (no API persistence)
		changeItems: {
			type: Function,
			required: false,
			default: null
		},
		selectionMode: {
			type: Boolean,
			required: false,
			default: false
		},
		beforeNavigate: {
			type: Function,
			required: false,
			default: null
		}
	},
	computed: {
		isSelectionMode(){
			return this.selectionMode || !!this.changeItems
		},
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject;
    },
		itemAssociations(){
			if(!this.item || !this.item.item_associations){
				return []
			}
			const types = {
				'rdi': 'App\\Models\\Rdi',
				'task': 'App\\Models\\TasksRestrictions',
				'additional': 'App\\Models\\Additional',
				'entrega': 'App\\Models\\Entrega',
				'agp': 'App\\Models\\AdditionalsGlobalPaymentStatus',
				'file': 'App\\Models\\FilesCenter',
			}
			const types_b = {
				'App\\Models\\Additional': 'Adicional',
				'App\\Models\\Rdi': 'RDI',
				'App\\Models\\TasksRestrictions': 'Tarea',
				'App\\Models\\Entrega': 'Entrega',
				'App\\Models\\AdditionalsGlobalPaymentStatus': 'Estado de Pago',
				'App\\Models\\File': 'Archivo',
				'App\\Models\\FilesCenter': 'Archivo',
			}
			const classToType = {
				'App\\Models\\Rdi': 'rdi',
				'App\\Models\\TasksRestrictions': 'task',
				'App\\Models\\Additional': 'additional',
				'App\\Models\\Entrega': 'entrega',
				'App\\Models\\AdditionalsGlobalPaymentStatus': 'agp',
				'App\\Models\\File': 'file',
				'App\\Models\\FilesCenter': 'file',
			}
			
			const getAssociatedItem = (association) => {
				const isFirstMorphable = association.first_morphable_type == types[this.item_type] && association.first_morphable_id == this.item.id
				const associatedMorphable = isFirstMorphable ? association.second_morphable : association.first_morphable
				const associatedType = isFirstMorphable ? association.second_morphable_type : association.first_morphable_type
				
				if(associatedType == types['task'] &&
						associatedMorphable.private &&
						(
								!associatedMorphable.participants.some(p => p.user_id == this.$store.userStore.state.userInfo.id) &&
								associatedMorphable.created_by != this.$store.userStore.state.userInfo.id &&
								associatedMorphable.responsable != this.$store.userStore.state.userInfo.id
						)
				){
						return false
				}
				const type = classToType[associatedType]
				const number = associatedMorphable.number || associatedMorphable.id
				const text_type = associatedMorphable.is_posible_additional ? 'P. Adicional' : types_b[associatedType]
				
				return {...associatedMorphable, ...{association_id: association.id, text_type, type, number}}
			}
			
			const items = this.item.item_associations.map(getAssociatedItem).filter(item => item)
			return items
		},
		allAssociations(){
			// Normaliza selectionItems para que tengan la misma forma que itemAssociations
			if(this.isSelectionMode){
				return this.selectionItems.map(a => {
					const id = a.id || a.association_id || a.uid || null
					const association_id = a.association_id || id
					const title = a.title || a.name || a.filename || ''
					const text_type = a.text_type || this.getType(a.type) || ''
					const number = a.number || id
					return Object.assign({}, a, { id, association_id, title, text_type, number })
				})
			}
			return this.itemAssociations
		},
		textAssociations(){
			return this.allAssociations.filter(association => association.type !== 'file')
		},
		fileAssociations(){
			return this.allAssociations.filter(association => association.type === 'file')
		},
		visibleTextAssociations(){
			return this.textAssociations.slice(0, TEXT_INLINE_LIMIT)
		},
		visibleFileAssociations(){
			return this.fileAssociations.slice(0, FILE_INLINE_LIMIT)
		},
		hiddenCount(){
			const hiddenText = this.textAssociations.length - this.visibleTextAssociations.length
			const hiddenFiles = this.fileAssociations.length - this.visibleFileAssociations.length
			return Math.max(0, hiddenText) + Math.max(0, hiddenFiles)
		}
	},
	data: () => ({
		nudgeLeft: 0,
		associationsMenu: false,
		searchForAssociation: null,
		showResults: false,
		searchResultsCount: 0,
		searching: false,
		searchResult: {},
		showFileSelector: false,
		showAssociationsPanel: false,
		viewerMediaItems: [],
		viewerMediaIndex: null,
		viewerDocumentItem: null,
		viewerForgeItem: null,
		forgeSupportedExtensions: [],
		forgeConfirmModal: false,
		forgeConfirmData: null,
		forgeItemToSend: null,
		// used only in selection mode to track chosen associations in-memory
		selectionItems: [],
	}),
	mounted(){
		this.$nextTick(() => {
			if (this.$vuetify.breakpoint.mobile && this.$el) {
				const rect = this.$el.getBoundingClientRect()
				this.nudgeLeft = rect.left
			}
		})
		this.getForgeSupportedExtensions()
	},
	methods: {
		checkPermission(permissionsToCheck, actionName) {
			if (!checkPermissions(Array.isArray(permissionsToCheck) ? permissionsToCheck : [permissionsToCheck])) {
				this.$store.globalStore.dispatch('addMessage', {
					message: `No tienes permisos para ${actionName || 'realizar esta acción'}.`,
					title: 'Acceso denegado',
					created: new Date(),
					type: 'warning',
					icon: 'mdi-lock-outline',
					color: '#FB8500',
				})
				return false
			}
			return true
		},
		getForgeSupportedExtensions(){
			getSupportedExtensions().then(response => {
				this.forgeSupportedExtensions = response.supported_extensions.map(ext => `${ext}`)
			})
		},
		isForgeItem(item){
			return isForgeCompatible(item, this.forgeSupportedExtensions)
		},
		promptSendToForge(association){
			if (!this.checkPermission('send_to_autodesk', 'enviar a visor Autodesk')) {
				return
			}
			this.forgeItemToSend = association
			this.forgeConfirmData = {
				title: 'Enviar a visor Autodesk',
				content_a: `¿Deseas enviar "${association.name}" al visor Autodesk?`,
				content_b: 'El archivo será procesado y podrá visualizarse después.'
			}
			this.forgeConfirmModal = true
		},
		async sendItemToForge(item){
			const response = await addFileToForge({file_id: item.id})
			if(response.code === 200){
				const fileName = item.name || item.title || item.filename || `Archivo ${item.id}`
				this.$store.globalStore.commit('setForgeProcessingFile', {
					file_id: item.id,
					file_name: fileName,
					title: fileName
				})
				this.reloadViewingItem()
			}
		},
		handleForgeConfirmation(confirmed){
			if(confirmed && this.forgeItemToSend){
				this.sendItemToForge(this.forgeItemToSend)
			}
			this.forgeConfirmModal = false
			this.forgeConfirmData = null
			this.forgeItemToSend = null
		},
		clearSearch(){
			this.searchForAssociation = null
			this.showResults = false
			this.searchResult = {}
		},
		openFileSelector(){
			this.associationsMenu = false
			this.showFileSelector = true
		},
		async onFileSelected(file){
			this.showFileSelector = false
			await this.addFileAssociation(file)
		},
		async addFileAssociation(file){
			if(!file){
				return
			}

			// In selection mode we don't persist: add the file to selectionItems in-memory
			if(this.isSelectionMode){
				const assoc = {
					id: file.id,
					type: 'file',
					title: file.name || file.title || file.filename || `Archivo ${file.id}`,
					name: file.name,
					ext: file.ext,
					uid: file.uid,
					pathdoc: file.pathdoc,
					pathmedia: file.pathmedia
				}
				const exists = this.selectionItems.some(item => item.id == assoc.id && item.type == assoc.type)
				if(!exists){
					this.selectionItems.push(assoc)
					if(this.changeItems){
						this.changeItems(this.selectionItems)
					}
				}
				return
			}

			// Persistent mode: guard against missing this.item
			if(!this.item){
				console.warn('addFileAssociation called but item is null')
				return
			}

			const data = {
				first_item_type: this.item_type,
				first_item_id: this.item.id,
				second_item_type: 'file',
				second_item_id: file.id
			}
			await addItemsAssociations(data)
			this.reloadViewingItem()
		},
		handlePanelOpenText(association){
			this.showAssociationsPanel = false
			this.handleMoveTo(association)
		},
		handlePanelOpenFile(association){
			this.showAssociationsPanel = false
			this.openFileAssociation(association)
		},
		handlePanelRemove(association){
			this.removeAssociation(association)
		},
		openFileAssociation(association){
			if(association.pathmedia){
				this.viewerMediaItems = [{
					src: association.pathmedia.path,
					ext: association.ext,
					size: association.size,
					name: association.name,
					uid: association.uid,
					autoplay: true,
					item: association
				}]
				this.viewerMediaIndex = 0
			}
			else if(association.pathdoc){
				this.viewerDocumentItem = association
			}
			else if(association.forge_file){
				if(association.forge_file.translated){
					this.viewerForgeItem = association
				}
				else{
					this.promptSendToForge(association)
				}
			}
			else if(this.isForgeItem(association)){
				this.promptSendToForge(association)
			}
			else{
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Este archivo no tiene un visor disponible.',
					title: 'Vista previa no disponible',
					created: new Date(),
					type: 'warning',
					icon: 'mdi-eye-off-outline',
					color: '#FB8500',
				})
			}
		},
		closeViewers(){
			this.viewerMediaItems = []
			this.viewerMediaIndex = null
			this.viewerDocumentItem = null
			this.viewerForgeItem = null
		},
		minimizeViewerFile(item){
			this.$store.globalStore.commit('addFileToFileManagerHandler', item)
			this.closeViewers()
		},
		onChangeMediaIndex(){},
		onViewerError(){
			console.error('Document failed to load')
		},
		onViewerLoad(){},
		async openItemMorphable(item){
			const typeMap = {
				'App\\Models\\TasksRestrictions': 'task',
				'App\\Models\\TasksRestrictionsInteractions': 'task_interaction',
				'App\\Models\\Rdi': 'rdi',
				'App\\Models\\RdiInteractions': 'rdi_interaction',
				'App\\Models\\Additional': 'additional',
				'App\\Models\\AdditionalRevision': 'additional_revision',
				'App\\Models\\AdditionalObservation': 'additional_observation'
			}
			const parentTypesMap = {
				'App\\Models\\TasksRestrictions': 'task',
				'App\\Models\\TasksRestrictionsInteractions': 'task',
				'App\\Models\\Rdi': 'rdi',
				'App\\Models\\RdiInteractions': 'rdi',
				'App\\Models\\Additional': 'additional',
				'App\\Models\\AdditionalRevision': 'additional',
				'App\\Models\\AdditionalObservation': 'additional'
			}
			if(!typeMap[item.morphable_type]){
				return
			}
			const data = {
				item_id: item.morphable_id,
				item_type: typeMap[item.morphable_type]
			}
			if(parentTypesMap[item.morphable_type] == 'task'){
				this.$store.consultasStore.commit('setCurrentTarea', { loading: true })
			}
			else if(parentTypesMap[item.morphable_type] == 'rdi'){
				this.$store.rdiStore.commit('setCurrentRDI', { loading: true })
			}
			else if(parentTypesMap[item.morphable_type] == 'additional'){
				this.$store.adicionalesStore.commit('setCurrentAdicional', { loading: true })
			}
			this.closeViewers()

			const element = await getParentElement(data)
			if(element.code == 200){
				if(parentTypesMap[item.morphable_type] == 'task'){
					this.$store.consultasStore.commit('setCurrentTarea', element.task)
				}
				else if(parentTypesMap[item.morphable_type] == 'rdi'){
					this.$store.rdiStore.commit('setCurrentRDI', element.rdi)
				}
				else if(parentTypesMap[item.morphable_type] == 'additional'){
					this.$store.adicionalesStore.commit('setCurrentAdicional', element.additional)
				}
			}
		},
		async handleMoveTo(association){
			if(this.beforeNavigate){
				await this.beforeNavigate()
			}
			else{
				this.$emit('closeParent')
			}
			this.$nextTick(() => {
				switch (association.type) {
					case 'rdi':
						this.viewRDI(association.id)
						break;
					case 'task':
						this.viewTarea(association.id)
						break;
					case 'entrega':
						this.viewEntrega(association.id)
						break;
					case 'additional':
						this.viewAdditional(association.id, true)
						break;
					case 'agp':
						this.$router.push({name: 'Adicionales', params: {listing_type: 3}})
						break;
					default:
						break;
				}
			})
		},
		startRemoveAssociation(itemToRemove){
			const association = this.itemAssociations.find(association => association.id == itemToRemove.id && association.type == itemToRemove.type)
			if(association){
				this.removeAssociation(association)
			}
		},
		startRemoveSelection(itemToRemove){
			this.selectionItems = this.selectionItems.filter(
				item => !(item.id === itemToRemove.id && item.type === itemToRemove.type)
			)
			if(this.changeItems){
				this.changeItems(this.selectionItems)
			}
		},
		handleRemoveAssociation(association){
			if(this.isSelectionMode){
				this.startRemoveSelection(association)
			}
			else{
				this.removeAssociation(association)
			}
		},
		setSelectedItems(associations){
			this.selectionItems = Array.isArray(associations) ? associations : []
		},
		getType(association_type){
			const types = {
				'rdi': 'RDI',
				'task': 'Tarea',
				'additional': 'Adicional',
				'file': 'File'
			}
			return types[association_type]
		},
		async removeAssociation(association){
			if(this.isSelectionMode){
				this.selectionItems = this.selectionItems.filter(
					item => !(item.id === association.id && item.type === association.type)
				)
				if(this.changeItems){
					this.changeItems(this.selectionItems)
				}
				return
			}
			await removeItemsAssociations({association_id: association.association_id})
			this.reloadViewingItem()
		},
		async closeResults(info = null){
			if(info){
				if(this.isSelectionMode){
					const exists = this.selectionItems.some(item => item.id == info.id && item.type == info.type)
					if(!exists){
						this.selectionItems.push(info)
					}
					if(this.changeItems){
						this.changeItems(this.selectionItems)
					}
				}
				else{
					const data = {
						first_item_type: this.item_type,
						first_item_id: this.item.id,
						second_item_type: info.type,
						second_item_id: info.id
					}
					await addItemsAssociations(data)
					this.reloadViewingItem()
				}
			}
			this.searching = false
		},
		reloadViewingItem(){
			switch (this.item_type) {
				case 'rdi':
					this.viewRDI(this.item.id)
					break;
				case 'task':
					this.viewTarea(this.item.id, true)
					break;
				case 'additional':
					this.viewAdditional(this.item.id, true)
					break;
				case 'entrega':
					this.viewEntrega(this.item.id, true)
					break;
				default:
					break;
			}
		},
		async viewAdditional(additional_id, dontSetLoading = false){
			this.$store.rdiStore.commit('setCurrentRDI', null)
			this.$store.consultasStore.commit('setCurrentTarea', null)
			this.$store.adicionalesStore.dispatch('getAdditionalByID', {additional_id, seen_by_me: true, dontSetLoading})
		},
		async viewRDI(rdi_id){
			this.$store.rdiStore.commit('setCurrentRDI', { loading: true })
			const rdi = await getRdiByID({rdi_id})
			if(rdi.code === 200){
				this.$store.rdiStore.commit('setCurrentRDI', rdi.rdi)
			}
		},
		async viewTarea(task_id){
			this.$store.consultasStore.commit('setCurrentTarea', { loading: true })
			const tarea = await getTareaByID({task_id})
			if(tarea.code === 200){
				this.$store.consultasStore.commit('setCurrentTarea', tarea.task)
			}
		},
    async viewEntrega(entregaId) {
      if (!this.selectedProject) return;
      const projectID = this.selectedProject.id;
      this.$store.entregaStore.commit("setCurrentEntrega", { loading: true });
      try {
        const entregaResponse = await getEntregaByID({ entrega_id: entregaId });
        const [configResponse, jobTypesResponse] = await Promise.all([
          getConfig({ project_id: projectID }),
          getJobTypes({ project_id: projectID }),
        ]);
        this.$store.usersStore.commit("setJobTypes", jobTypesResponse.job_types);
        this.$store.entregaStore.commit("setConfig", configResponse.config);
				if (entregaResponse.code === 403) {
					this.$store.entregaStore.commit("setCurrentEntrega", null);
				} else if (entregaResponse.code === 200) {
          this.$store.entregaStore.commit("setCurrentEntrega", entregaResponse.entrega);
        }
      } catch (error) {
        this.$store.entregaStore.commit("setCurrentEntrega", { loading: false, error: true });
      }
    },
    sendToSearch: _debounce(function(){ this.searchForString(this.searchForAssociation) }, 800),
		async searchForString(searchString){
			this.searching = true
			const data = {
				search: searchString,
				project_id: this.$store.projectsStore.state.selectedProject.id,
				exclude_description: true
			}
			const searchResult = await search(data)
			if(searchResult.code == 200){
				this.searchResult = searchResult
				this.searchResultsCount = searchResult.tasks.length + searchResult.rdis.length
				this.showResults = true
			}
			this.searching = false
		},
	}
};
</script>

<style scoped>
.meta-label-with-action {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 8px;
	margin-bottom: 8px;
}

.meta-label {
	font-size: 11px;
	font-weight: 600;
	color: #6B7280;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin: 0;
}


.icon-action-btn {
  width: 28px; height: 28px; border-radius: 7px;
  background: #EEF2FF; border: none; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 0.12s;
}
.icon-action-btn:hover { background: #dde5f9; }
.icon-action-btn.success { background: #DCFCE7; }
.icon-action-btn.success:hover { background: #BBF7D0; }
.icon-action-btn.danger  { background: #FEE2E2; }
.icon-action-btn.danger:hover  { background: #FECACA; }

/* Botón ampliado para modo selección */
.add-assoc-btn {
	/* expandimos tamaño para modo selección */
	width: auto;
	min-width: 160px;
	height: 36px;
	background: #5E85EF;
	color: #ffffff;
	padding: 8px 12px;
	border-radius: 8px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-weight: 600;
}
.add-assoc-btn:hover { background: #4b6fd1; }
.add-assoc-content {
	color: #ffffff;
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
}
.search-dropdown {
	background: #ffffff;
	width: 420px;
	border-radius: 10px;
	box-shadow: 0 8px 24px rgba(45, 75, 149, 0.12);
}
.search-field /deep/ .v-input__slot {
  border-radius: 8px !important;
  height: 36px !important;
  min-height: 36px !important;
  display: flex !important;
  align-items: center !important;
}

/* Centrado del label/placeholder */
.search-field /deep/ .v-label {
  top: 50% !important;
  transform: translateY(-50%) !important;
  margin-top: 0 !important;
  line-height: 1 !important;
}

/* Cuando el label flota (campo activo/con valor) */
.search-field /deep/ .v-label--active {
  transform: translateY(-18px) scale(0.75) !important;
}

/* Input nativo */
.search-field /deep/ input {
  align-self: center !important;
  margin-top: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  line-height: 36px !important;
}

/* Iconos prepend y append */
.search-field /deep/ .v-input__prepend-inner,
.search-field /deep/ .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
  padding-top: 0 !important;
  display: flex !important;
  align-items: center !important;
  height: 100% !important;
}
.search-field /deep/ .v-label--active {
  transform: translateY(-24px) scale(0.75) !important;
}

/* Estado vacío del buscador de asociaciones */
.assoc-empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: 6px;
	padding: 18px 8px 10px;
}

.assoc-empty-state__text {
	font-size: 12px;
	color: #8b94ad;
	line-height: 1.4;
	max-width: 280px;
}

.assoc-divider {
	display: flex;
	align-items: center;
	width: 100%;
	margin: 10px 0 8px;
	color: #c2cbe0;
	font-size: 11px;
}
.assoc-divider::before,
.assoc-divider::after {
	content: '';
	flex: 1;
	height: 1px;
	background: #e7ebf5;
}
.assoc-divider span {
	padding: 0 8px;
}

.assoc-file-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 9px 10px;
	border-radius: 8px;
	border: 1.5px dashed #c5d3f8;
	background: #f7f9ff;
	color: #2D4B95;
	font-size: 12.5px;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.12s, border-color 0.12s;
}
.assoc-file-btn:hover {
	background: #eef2ff;
	border-color: #5e85ef;
}

/* Contenedor general del contenido de asociaciones */
.assoc-content {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

/* Fila de archivos (miniaturas/íconos) */
.files-wrapper {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
}

/* Fila de pills textuales */
.chips-wrapper {
	display: flex;
	flex-wrap: wrap;
	gap: 5px;
}

.assoc-more-btn {
	align-self: flex-start;
	display: flex;
	align-items: center;
	border: none;
	background: transparent;
	color: #5e85ef;
	font-size: 11px;
	font-weight: 600;
	cursor: pointer;
	padding: 2px 4px;
	border-radius: 6px;
}
.assoc-more-btn:hover {
	background: #f0f4ff;
	text-decoration: underline;
}

@media (max-width: 600px) {
  .search-dropdown {
    width: 100vw !important;
    max-width: 100vw !important;
    border-radius: 0 0 10px 10px;
    box-shadow: 0 8px 24px rgba(45, 75, 149, 0.12);
  }
}
</style>